import api from './api';

/**
 * Obtiene los unit templates disponibles para comprar
 * @param {string} gameId - El ID del juego
 * @returns {Promise<object>} Los unit templates disponibles con precios
 */
export const getAvailableUnitTemplates = async (gameId) => {
  const response = await api.get(`/api/games/${gameId}/shop/available-units`);
  return response.data;
};

/**
 * Obtiene las unidades compradas (en roster) del jugador
 * @param {string} gameId - El ID del juego
 * @returns {Promise<object>} Las unidades compradas
 */
export const getPurchasedUnits = async (gameId) => {
  const response = await api.get(`/api/games/${gameId}/shop/purchased-units`);
  return response.data;
};

/**
 * Compra una unidad y la añade al roster del jugador
 * @param {string} gameId - El ID del juego
 * @param {number} unitTemplateId - El ID del template de la unidad
 * @returns {Promise<object>} La respuesta con la unidad comprada y el nuevo balance
 */
export const buyUnitToRoster = async (gameId, unitTemplateId) => {
  const response = await api.post(`/api/games/${gameId}/shop/buy-unit-roster`, {
    unit_template_id: unitTemplateId
  });
  return response.data;
};

/**
 * Reembolsa una unidad y recupera dinero
 * @param {string} gameId - El ID del juego
 * @param {number} unitId - El ID de la unidad a reembolsar
 * @returns {Promise<object>} La respuesta con el monto reembolsado y nuevo balance
 */
export const refundUnit = async (gameId, unitId) => {
  const response = await api.post(`/api/games/${gameId}/shop/refund-unit/${unitId}`);
  return response.data;
};

/**
 * Reembolsa todas las unidades del roster
 * @param {string} gameId - El ID del juego
 * @returns {Promise<object>} La respuesta con el monto total reembolsado y nuevo balance
 */
export const refundAllUnits = async (gameId) => {
  const response = await api.post(`/api/games/${gameId}/shop/refund-all`);
  return response.data;
};

// =========================================================================
// LOADOUT TEMPLATES
// =========================================================================

/**
 * Get available loadout templates for the player
 * @param {string} gameId - The game ID
 * @returns {Promise<object>} Loadout templates (flat and grouped by category)
 */
export const getLoadoutTemplates = async (gameId) => {
  const response = await api.get(`/api/games/${gameId}/shop/loadout-templates`);
  return response.data;
};

/**
 * Apply a loadout template to the player's roster
 * @param {string} gameId - The game ID
 * @param {number|null} loadoutTemplateId - The loadout template ID (null for manual/clear)
 * @returns {Promise<object>} Updated roster and balance
 */
export const applyLoadout = async (gameId, loadoutTemplateId) => {
  const response = await api.post(`/api/games/${gameId}/shop/apply-loadout`, {
    loadout_template_id: loadoutTemplateId
  });
  return response.data;
};

/**
 * Switch to custom_unsaved category without modifying units or money
 * Used when user clicks "Custom" tab from another category
 * @param {string} gameId - The game ID
 * @returns {Promise<object>} Updated loadout category
 */
export const switchToCustom = async (gameId) => {
  const response = await api.post(`/api/games/${gameId}/shop/switch-to-custom`);
  return response.data;
};

/**
 * Create a new player loadout template
 * @param {string} gameId - The game ID
 * @param {object} data - { name: string, units: [{unit_template_slug, quantity}] }
 * @returns {Promise<object>} Created loadout template
 */
export const createLoadoutTemplate = async (gameId, data) => {
  const response = await api.post(`/api/games/${gameId}/shop/loadout-templates`, data);
  return response.data;
};

/**
 * Update a player loadout template
 * @param {string} gameId - The game ID
 * @param {number} loadoutTemplateId - The loadout template ID
 * @param {object} data - { name?: string, units?: [{unit_template_slug, quantity}] }
 * @returns {Promise<object>} Updated loadout template
 */
export const updateLoadoutTemplate = async (gameId, loadoutTemplateId, data) => {
  const response = await api.put(`/api/games/${gameId}/shop/loadout-templates/${loadoutTemplateId}`, data);
  return response.data;
};

/**
 * Delete a player loadout template
 * @param {string} gameId - The game ID
 * @param {number} loadoutTemplateId - The loadout template ID
 * @returns {Promise<object>} Success response
 */
export const deleteLoadoutTemplate = async (gameId, loadoutTemplateId) => {
  const response = await api.delete(`/api/games/${gameId}/shop/loadout-templates/${loadoutTemplateId}`);
  return response.data;
};